import { Button, Grid, Typography } from '@mui/material'
import get from 'lodash/get'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { isNotFoundApiError } from '@omnea-app/legacy/utils/error-handling/isApiErrorOfType'
import { NotFoundBoundary } from './NotFoundBoundary'
import { OrganisationSwitchBoundary } from './OrganisationSwitchBoundary'
import type { TranslationFile } from '@omnea-app/packages/universal/i18n/translationFiles'

type ResourceTypeStringKey = Extract<keyof TranslationFile<'common'>['organisation-switch-boundary']['resourceTypes'], string>

interface CustomErrorBoundaryProps {
  errorData: { error: unknown }
  resourceType?: ResourceTypeStringKey
}

// Note (Gavin): This component does not use style from the theme as errors can occur outside of the theme wrapper
export const CustomErrorBoundary = ({ errorData, resourceType }: CustomErrorBoundaryProps) => {
  const { t } = useTranslation('common')
  const { push, reload } = useRouter()

  if (isNotFoundApiError(errorData.error)) {
    const hintOrganisationId = get(errorData.error, 'detail.hintOrganisationId')
    if (typeof hintOrganisationId === 'string') {
      return <OrganisationSwitchBoundary correctOrganisationId={hintOrganisationId} resourceType={resourceType ?? 'request'} />
    }

    return <NotFoundBoundary />
  }

  const handleHomeButtonClicked = async () => {
    await push('/')
    reload()
  }

  return (
    <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
      <Grid
        container
        direction="column"
        sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          alignItems: 'center',
          textAlign: 'center',
          padding: '40px',
          width: '480px',
        }}
      >
        <Image src="/app/assets/error-image.svg" alt="error" height={160} width={280} />
        <Grid>
          <Typography sx={{ mt: '30px', color: '#111116', fontSize: '25px', fontWeight: 600 }}>
            {t('error-boundary.title')}
          </Typography>
        </Grid>
        <Grid>
          <Typography sx={{ mt: '15px', color: '#6B6C7E', fontSize: '14px', fontWeight: 400 }}>
            {t('error-boundary.subtext1')}
          </Typography>
        </Grid>
        <Button sx={{ mt: '20px' }} variant="outlined" color="neutral" onClick={() => handleHomeButtonClicked()}>
          {t('error-boundary.return-home-button')}
        </Button>
      </Grid>
    </Grid>
  )
}
